import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "./api";
import { useCart } from "./cart";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const cart = useCart();

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("mps_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // 401 here just means the stored token is invalid/expired — expected on session boundary.
      if (process.env.NODE_ENV !== "production" && err?.response?.status !== 401) {
        console.warn("auth.refresh failed:", err);
      }
      localStorage.removeItem("mps_token");
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("mps_token", data.access_token);
    setUser(data.user);
    try { await cart?.mergeOnAuth?.(); } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("auth.login cart merge failed:", err);
    }
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("mps_token", data.access_token);
    setUser(data.user);
    try { await cart?.mergeOnAuth?.(); } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("auth.register cart merge failed:", err);
    }
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("mps_token");
    setUser(null);
    try { cart?.refresh?.(); } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("auth.logout cart refresh failed:", err);
    }
  };

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh }),
    [user, loading], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
